import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Token storage for authentication
const TOKEN_KEY = "auth_token_backup";

// Safe localStorage access (Safari private mode may block it)
const getStoredToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (e) {
    console.warn("localStorage not available:", e);
    return null;
  }
};

const setStoredToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch (e) {
    console.warn("localStorage not available:", e);
  }
};

// In-memory token fallback for when localStorage is blocked
let memoryToken = null;
let memoryRefreshToken = null;
const REFRESH_KEY = "auth_refresh_token_backup";

const getToken = () => {
  return getStoredToken() || memoryToken;
};

const getRefreshToken = () => {
  try { return localStorage.getItem(REFRESH_KEY) || memoryRefreshToken; } catch { return memoryRefreshToken; }
};

const saveToken = (token, refreshToken) => {
  memoryToken = token;
  setStoredToken(token);
  if (refreshToken) {
    memoryRefreshToken = refreshToken;
    try { localStorage.setItem(REFRESH_KEY, refreshToken); } catch {}
  }
};

const clearToken = () => {
  memoryToken = null;
  memoryRefreshToken = null;
  setStoredToken(null);
  try { localStorage.removeItem(REFRESH_KEY); } catch {}
};

// Silent session renewal (sliding session). Uses the httpOnly cookie when
// available, or the stored refresh token as fallback. Returns true on success.
const tryRefreshSession = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ refresh_token: getRefreshToken() }),
      mode: 'cors',
      credentials: 'include',
    });
    if (!response.ok) return false;
    const data = await response.json();
    if (data.access_token) saveToken(data.access_token, data.refresh_token);
    console.log("[AUTH] Session refreshed");
    return true;
  } catch (e) {
    console.warn("[AUTH] Session refresh failed:", e.message);
    return false;
  }
};

// Universal fetch POST helper - works on all devices including iOS Safari 18+
const fetchPost = async (url, data = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
    mode: 'cors',
  });
  
  const responseData = await response.json();
  
  if (!response.ok) {
    const error = new Error(responseData.detail || 'Request failed');
    error.response = { data: responseData, status: response.status };
    throw error;
  }
  
  return { data: responseData };
};

// Universal fetch GET helper
const fetchGet = async (url) => {
  const token = getToken();
  const headers = {
    'Accept': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    method: 'GET',
    headers,
    mode: 'cors',
  });
  
  const responseData = await response.json();
  
  if (!response.ok) {
    const error = new Error(responseData.detail || 'Request failed');
    error.response = { data: responseData, status: response.status };
    throw error;
  }
  
  return { data: responseData };
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  const googleAuthProcessed = useRef(false);

  // Format API error detail and translate common messages
  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Algo deu errado. Tente novamente.";
    if (typeof detail === "string") {
      const translations = {
        "Email already registered": "Este email já está cadastrado. Tente fazer login.",
        "Invalid email or password": "Email ou senha incorretos.",
        "User not found": "Usuário não encontrado.",
        "Invalid password": "Senha incorreta.",
        "Not authenticated": "Você precisa estar logado para acessar.",
        "Password change only available for email accounts": "Alteração de senha disponível apenas para contas de email.",
        "Current password is incorrect": "A senha atual está incorreta.",
        "No active PRO subscription to cancel": "Nenhuma assinatura PRO ativa para cancelar.",
        "VIP subscriptions cannot be cancelled": "Assinaturas VIP não podem ser canceladas.",
        "Guests cannot update profile": "Convidados não podem atualizar o perfil.",
        "Guest accounts cannot be deleted": "Contas de convidado não podem ser excluídas."
      };
      return translations[detail] || detail;
    }
    if (Array.isArray(detail))
      return detail
        .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  // Process Google OAuth session_id from URL hash
  const processGoogleAuth = useCallback(async (sessionId) => {
    if (googleAuthProcessed.current) return null;
    googleAuthProcessed.current = true;
    
    try {
      const response = await fetchPost(`${BACKEND_URL}/api/auth/google`, { session_id: sessionId });
      
      // Clear the hash from URL without reload
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      
      if (response.data.access_token) {
        saveToken(response.data.access_token, response.data.refresh_token);
      }
      
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error("Google auth error:", error);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) };
    }
  }, []);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    const token = getToken();
    if (!token) return { success: false };
    
    try {
      const response = await fetchGet(`${BACKEND_URL}/api/auth/me`);
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error("[AUTH] refreshUser failed:", error.message);
      return { success: false };
    }
  }, []);

  // Check if user is authenticated on mount
  const checkAuth = useCallback(async () => {
    // Skip if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Check if we have a stored token first
    const token = getToken();
    console.log("[AUTH] checkAuth called, token exists:", !!token);
    
    if (!token) {
      // No token, user is not authenticated
      console.log("[AUTH] No token found, setting user to false");
      setUser(false);
      setLoading(false);
      return;
    }
    
    try {
      console.log("[AUTH] Fetching /api/auth/me with token");
      const response = await fetchGet(`${BACKEND_URL}/api/auth/me`);
      console.log("[AUTH] /api/auth/me success:", response.data?.user?.email);
      setUser(response.data.user);
    } catch (error) {
      console.error("[AUTH] /api/auth/me failed:", error.message);
      // Access token expired/invalid → try a silent refresh before giving up
      if (await tryRefreshSession()) {
        try {
          const retry = await fetchGet(`${BACKEND_URL}/api/auth/me`);
          setUser(retry.data.user);
          setLoading(false);
          return;
        } catch (e) {
          console.error("[AUTH] /api/auth/me failed after refresh:", e.message);
        }
      }
      clearToken();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Keep the session alive while the app is open (event can last many hours):
  // renew every 30 min and whenever the tab becomes visible again.
  useEffect(() => {
    if (!user) return;
    const renew = () => { if (getToken()) tryRefreshSession(); };
    const id = setInterval(renew, 30 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === "visible") renew(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, [user]);

  // Register with email and password
  const register = async (email, password, name) => {
    console.log("[AUTH] Register attempt for:", email);
    try {
      const response = await fetchPost(`${BACKEND_URL}/api/auth/register`, { email, password, name });
      console.log("[AUTH] Register response:", response.data ? "success" : "no data");
      if (response.data.access_token) {
        saveToken(response.data.access_token, response.data.refresh_token);
        console.log("[AUTH] Token saved");
      }
      setUser(response.data.user);
      console.log("[AUTH] User set:", response.data.user?.email);
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error("[AUTH] Register error:", error.message);
      const message = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: message };
    }
  };

  // Login with email and password
  const login = async (email, password) => {
    console.log("[AUTH] Login attempt for:", email);
    try {
      const response = await fetchPost(`${BACKEND_URL}/api/auth/login`, { email, password });
      console.log("[AUTH] Login response:", response.data ? "success" : "no data");
      if (response.data.access_token) {
        saveToken(response.data.access_token, response.data.refresh_token);
        console.log("[AUTH] Token saved");
      }
      setUser(response.data.user);
      console.log("[AUTH] User set:", response.data.user?.email);
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error("[AUTH] Login error:", error.message);
      const message = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: message };
    }
  };

  // Login with wallet
  const walletLogin = async (walletAddress, signature, message) => {
    try {
      const response = await fetchPost(`${BACKEND_URL}/api/auth/wallet-login`, {
        wallet_address: walletAddress,
        signature,
        message,
      });
      if (response.data.access_token) {
        saveToken(response.data.access_token, response.data.refresh_token);
      }
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error("Wallet login error:", error);
      const message = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: message };
    }
  };

  // Guest login (1 minute limit)
  const guestLogin = async () => {
    console.log("[AUTH] Guest login attempt");
    try {
      const response = await fetchPost(`${BACKEND_URL}/api/auth/guest`, {});
      console.log("[AUTH] Guest login response:", response.data ? "success" : "no data");
      if (response.data.access_token) {
        saveToken(response.data.access_token, response.data.refresh_token);
        console.log("[AUTH] Token saved");
      }
      setUser(response.data.user);
      console.log("[AUTH] User set:", response.data.user?.email || response.data.user?.guest_id);
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error("[AUTH] Guest login error:", error.message);
      const message = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: message };
    }
  };

  // Update usage (for Free users)
  const updateUsage = async (secondsUsed) => {
    try {
      const response = await fetchPost(`${BACKEND_URL}/api/auth/update-usage`, { seconds_used: secondsUsed });
      if (response.data.user) {
        setUser(response.data.user);
      }
      return { success: true };
    } catch (error) {
      console.error("Update usage error:", error);
      return { success: false };
    }
  };

  // Update user profile
  const updateProfile = async (updates) => {
    try {
      const response = await fetchPost(`${BACKEND_URL}/api/auth/update-profile`, updates);
      if (response.data.user) {
        setUser(response.data.user);
      }
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error("Update profile error:", error);
      const message = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: message };
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await fetchPost(`${BACKEND_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      return { success: true };
    } catch (error) {
      console.error("Change password error:", error);
      const message = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: message };
    }
  };

  // Cancel subscription
  const cancelSubscription = async () => {
    try {
      const response = await fetchPost(`${BACKEND_URL}/api/auth/cancel-subscription`, {});
      if (response.data.user) {
        setUser(response.data.user);
      }
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error("Cancel subscription error:", error);
      const message = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: message };
    }
  };

  // Delete account
  const deleteAccount = async () => {
    try {
      await fetchPost(`${BACKEND_URL}/api/auth/delete-account`, {});
      setUser(false);
      clearToken();
      return { success: true };
    } catch (error) {
      console.error("Delete account error:", error);
      const message = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: message };
    }
  };

  // Logout
  const logout = async () => {
    try {
      await fetchPost(`${BACKEND_URL}/api/auth/logout`, {});
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(false);
    clearToken();
    try {
      localStorage.removeItem("translation_source_lang");
      localStorage.removeItem("translation_target_lang");
      localStorage.removeItem("currentTranslation");
      localStorage.removeItem("translationTimestamp");
    } catch (e) {
      console.warn("Could not clear localStorage:", e);
    }
  };

  // Calculate remaining seconds for non-PRO users
  const userPlan = user ? (user.plan_id || user.plan) : null;
  const remainingSeconds = user
    ? userPlan === "pro" || user.is_vip
      ? -1 // Unlimited
      : userPlan === "guest"
      ? Math.max(0, 60 - (user.usage_seconds || 0)) // 1 minute for guests
      : Math.max(0, 300 - (user.usage_seconds || 0)) // 5 minutes for free users
    : 0;

  const isPro = user && (userPlan === "pro" || user.is_vip);
  const isGuest = user && userPlan === "guest";
  
  // CRITICAL: isAuthenticated must be explicitly calculated
  // user === null means still checking, user === false means not authenticated
  // user === {object} means authenticated
  const isAuthenticated = !!user && user !== false;

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    walletLogin,
    guestLogin,
    updateUsage,
    updateProfile,
    changePassword,
    cancelSubscription,
    deleteAccount,
    processGoogleAuth,
    refreshUser,
    isPro,
    isGuest,
    remainingSeconds,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
